import { Link as RouterLink } from 'react-router-dom'
import { css } from '@emotion/react'
import omit from 'lodash/omit'
import qs from 'query-string'

import { getTaskworldFrontendUrl } from '../services/Env'
import useWorkspaceId from '../services/useWorkspaceId'

type Props = React.HTMLAttributes<HTMLElement> & {
  id?: string
  className?: string
  children: React.ReactNode
  to?:
    | string
    | {
        path: string
        hash?: string
        query?: { [key: string]: string | Array<string> | null }
        replace?: boolean
      }
  disabled?: boolean
  autoFocus?: boolean
}

const urlProtocolPattern = /^\w+:/

export default function ClickArea(props: Props) {
  const passThroughProps = getPrimitiveProps(props)

  if (props.to && !props.disabled) {
    const { url, query, hash, replace } = getUrlObject(props.to)

    // In case of an external link
    if (urlProtocolPattern.test(url)) {
      const queryString = qs.stringify(query)
      return (
        <a
          {...passThroughProps}
          href={url + (queryString ? '?' + queryString : '') + (hash || '')}
          target={replace ? undefined : '_blank'}
          rel={replace ? undefined : 'noopener noreferrer'}
          css={resetLinkStyle}
        />
      )
    }

    return (
      <LinkInternal
        passThroughProps={passThroughProps}
        url={url}
        query={query}
        hash={hash}
        replace={replace}
      />
    )
  }

  return (
    <button
      type="button"
      {...passThroughProps}
      css={css`
        font-family: inherit;
        font-size: inherit;
        background: none;
        color: inherit;
        border: none;
        outline: none;
        padding: 0;
      `}
      disabled={props.disabled}
      autoFocus={props.autoFocus}
    />
  )
}

function getPrimitiveProps(props: Props) {
  return omit(props, 'to', 'disabled', 'autoFocus')
}

function getUrlObject(to: NonNullable<Props['to']>) {
  const { url, query: existingQuery } = qs.parseUrl(typeof to === 'string' ? to : to.path)

  const hash = typeof to === 'string' ? to.match(/(#.*)?$/)![0] : to.hash
  const incomingQuery = typeof to === 'object' && to.query ? to.query : {}
  const query: qs.ParsedQuery<string> = {
    ...existingQuery,
    ...incomingQuery,
  }

  const replace =
    typeof to !== 'string' && typeof to.replace === 'boolean'
      ? to.replace
      : url.startsWith(getTaskworldFrontendUrl())

  return {
    url,
    query,
    hash,
    replace,
  }
}

function LinkInternal(
  props: {
    passThroughProps: Omit<
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
      'contentEditable' | 'draggable' | 'spellCheck'
    >
  } & ReturnType<typeof getUrlObject>,
) {
  const workspaceId = useWorkspaceId()

  return (
    <RouterLink
      {...props.passThroughProps}
      to={{
        pathname: props.url,
        search: qs.stringify({
          workspaceId,
          ...props.query,
        }),
        hash: props.hash,
      }}
      replace={props.replace}
      css={resetLinkStyle}
    />
  )
}

const resetLinkStyle = css`
  color: inherit;
`
